import { logger } from "@/utils/logger";
import type { Roles } from "./types";

// Cache for roles with timestamp
let rolesCache: {
  roles: Roles | null;
  timestamp: number;
  userId: string | null;
} = {
  roles: null,
  timestamp: 0,
  userId: null,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const STALE_DURATION = 10 * 60 * 1000; // 10 minutes - serve stale data while revalidating

// OPTIMIZATION: Request deduplication - prevent concurrent fetches
let pendingFetch: Promise<Roles> | null = null;

/**
 * Role management
 * Fetches and caches user roles with proper invalidation
 */
export const rolesManager: {
  fetchRoles: (forceRefresh?: boolean) => Promise<Roles>;
  getRoles: () => Roles | null;
  clearRoles: () => void;
} = {
  /**
   * Fetch roles from API
   * Uses caching and request deduplication to reduce API calls
   */
  fetchRoles: async (forceRefresh = false): Promise<Roles> => {
    const operationId = `roles-fetch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = Date.now();

    try {
      // OPTIMIZATION: Check cache first (unless force refresh)
      // Allow stale data to be returned immediately while revalidating in background
      const now = Date.now();
      const cacheAge = now - rolesCache.timestamp;
      const isFresh = cacheAge < CACHE_DURATION;
      const isStale = cacheAge < STALE_DURATION;

      if (!forceRefresh && rolesCache.roles) {
        if (isFresh) {
          logger.debug("Returning fresh cached roles", {
            component: "RolesManager",
            operationId,
            cacheAgeMs: cacheAge,
          });
          return rolesCache.roles;
        }

        // OPTIMIZATION: Return stale data immediately if available
        // This allows instant UI updates while fetching fresh data in background
        if (isStale) {
          logger.debug(
            "Returning stale cached roles, revalidating in background",
            {
              component: "RolesManager",
              operationId,
              cacheAgeMs: cacheAge,
            }
          );
          // Trigger background refresh but don't wait for it
          fetchRolesInBackground(forceRefresh).catch(() => {
            // Ignore background fetch errors
          });
          return rolesCache.roles;
        }
      }

      // OPTIMIZATION: Request deduplication - reuse pending fetch if one exists
      if (pendingFetch && !forceRefresh) {
        logger.debug("Reusing pending roles fetch", {
          component: "RolesManager",
          operationId,
        });
        return pendingFetch;
      }

      // Create new fetch promise
      const fetchPromise = (async () => {
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };

        // Force cache bypass when explicitly requested
        if (forceRefresh) {
          headers["Cache-Control"] = "no-cache";
          headers["Pragma"] = "no-cache";
        }

        logger.debug("Fetching roles from API", {
          component: "RolesManager",
          operationId,
          forceRefresh,
        });

        const fetchStartTime = Date.now();
        logger.info("🟠 RolesManager: Starting fetch to /api/user/roles", {
          component: "RolesManager",
          action: "fetch_start",
          metadata: {
            flow: "oauth",
            operationId,
            timeSinceStart: fetchStartTime - startTime,
            forceRefresh,
            timestamp: fetchStartTime,
          },
        });

        const response = await fetchWithTimeout(
          "/api/user/roles",
          {
            method: "GET",
            credentials: "include",
            cache: forceRefresh ? "no-store" : "default",
            headers,
          },
          5000 // 5 second timeout
        );

        const fetchDuration = Date.now() - fetchStartTime;
        logger.info("🟠 RolesManager: Fetch completed", {
          component: "RolesManager",
          action: "fetch_complete",
          metadata: {
            flow: "oauth",
            operationId,
            duration: fetchDuration,
            status: response.status,
            timeSinceStart: Date.now() - startTime,
            timestamp: Date.now(),
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            // User is not authenticated
            const defaultRoles: Roles = { admin: false, manufacturer: false };
            rolesCache = {
              roles: defaultRoles,
              timestamp: Date.now(),
              userId: null,
            };
            return defaultRoles;
          }
          throw new Error(
            `API error: ${response.status} ${response.statusText}`
          );
        }

        const result = await response.json();

        if (result.success && result.data) {
          const roles: Roles = result.data;
          rolesCache = {
            roles,
            timestamp: Date.now(),
            userId: null, // We don't track userId here, but could if needed
          };

          const duration = Date.now() - startTime;
          logger.debug("Roles fetched successfully", {
            component: "RolesManager",
            operationId,
            duration,
            roles,
            fromCache: response.headers.get("x-cache") === "HIT",
          });

          return roles;
        } else {
          const defaultRoles: Roles = { admin: false, manufacturer: false };
          rolesCache = {
            roles: defaultRoles,
            timestamp: Date.now(),
            userId: null,
          };
          return defaultRoles;
        }
      })();

      // Store pending fetch for deduplication
      pendingFetch = fetchPromise;

      try {
        const roles = await fetchPromise;
        return roles;
      } finally {
        // Clear pending fetch after completion
        if (pendingFetch === fetchPromise) {
          pendingFetch = null;
        }
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.warn(
        "Failed to fetch roles",
        {
          component: "RolesManager",
          operationId,
          duration,
        },
        error instanceof Error ? error : new Error(String(error))
      );

      // Return cached roles if available (even if stale), otherwise default
      if (rolesCache.roles) {
        logger.debug("Returning cached roles on error", {
          component: "RolesManager",
          operationId,
        });
        return rolesCache.roles;
      }

      // Return default roles on error
      const defaultRoles: Roles = { admin: false, manufacturer: false };
      rolesCache = {
        roles: defaultRoles,
        timestamp: Date.now(),
        userId: null,
      };
      return defaultRoles;
    }
  },

  /**
   * Get cached roles (synchronous)
   */
  getRoles: (): Roles | null => {
    if (
      rolesCache.roles &&
      rolesCache.timestamp > Date.now() - CACHE_DURATION
    ) {
      return rolesCache.roles;
    }
    return null;
  },

  /**
   * Clear roles cache
   */
  clearRoles: (): void => {
    rolesCache = {
      roles: null,
      timestamp: 0,
      userId: null,
    };
    pendingFetch = null; // Also clear pending fetch
    logger.debug("Roles cache cleared", {
      component: "RolesManager",
    });
  },
};

/**
 * OPTIMIZATION: Background fetch for stale-while-revalidate pattern
 * Fetches fresh data without blocking the caller
 */
async function fetchRolesInBackground(forceRefresh = false): Promise<void> {
  try {
    const response = await fetch("/api/user/roles", {
      method: "GET",
      credentials: "include",
      cache: forceRefresh ? "no-store" : "default",
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        rolesCache = {
          roles: result.data,
          timestamp: Date.now(),
          userId: null,
        };
        logger.debug("Background roles refresh completed", {
          component: "RolesManager",
        });
      }
    }
  } catch {
    // Silently fail background refresh - we already have stale data
    logger.debug("Background roles refresh failed", {
      component: "RolesManager",
    });
  }
}

const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeoutMs: number = 5000
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    throw error;
  }
};
